// src/components/Leave/LeaveEdit.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiUpload, FiCalendar } from 'react-icons/fi';
import { useAppContext } from '../../context/AppContext';

const LeaveEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { leave } = useAppContext();
  const { getMyRequests, updateLeave, loading } = leave;

  const [formData, setFormData] = useState({
    leave_type: '',
    start_date: '',
    end_date: '',
    reason: '',
    attachment: null,
    total_days: 0,
  });
  const [originalData, setOriginalData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [calculatedDays, setCalculatedDays] = useState(0);

  useEffect(() => {
    loadLeave();
  }, [id]);

  const loadLeave = async () => {
    setIsLoading(true);
    try {
      const data = await getMyRequests({ id });
      let leaveData;
      if (data && data.results) {
        leaveData = data.results.find(item => item.id === parseInt(id));
      } else if (data && data.id) {
        leaveData = data;
      }

      if (leaveData) {
        setFormData({
          leave_type: leaveData.leave_type || '',
          start_date: leaveData.start_date || '',
          end_date: leaveData.end_date || '',
          reason: leaveData.reason || '',
          attachment: null,
          total_days: leaveData.total_days || 0,
        });
        setCalculatedDays(leaveData.total_days || 0);
        setOriginalData(leaveData);
      }
    } catch (err) {
      setError(err.message || 'Failed to load leave');
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ CORRECTED: Calculate days between two dates
  const calculateDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;

    const start = new Date(startDate);
    const end = new Date(endDate);

    // Validate dates
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
    if (end < start) return 0;

    // ✅ Calculate difference in days (inclusive)
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

    return diffDays;
  };

  // ✅ Handle date change and recalculate total days
  const handleDateChange = (e) => {
    const { name, value } = e.target;

    // Update the specific date field
    setFormData(prev => {
      const newData = { ...prev, [name]: value };

      // Get both dates (use existing or new)
      const startDate = name === 'start_date' ? value : prev.start_date;
      const endDate = name === 'end_date' ? value : prev.end_date;

      // ✅ Recalculate total days
      const days = calculateDays(startDate, endDate);
      newData.total_days = days;
      setCalculatedDays(days);

      return newData;
    });
  };

  // ✅ Handle other field changes
  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === 'attachment') {
      setFormData(prev => ({ ...prev, attachment: files[0] || null }));
    } else if (name === 'leave_type' || name === 'reason') {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsSubmitting(true);

    try {
      const data = new FormData();
      if (formData.leave_type) data.append('leave_type', formData.leave_type);
      if (formData.start_date) data.append('start_date', formData.start_date);
      if (formData.end_date) data.append('end_date', formData.end_date);
      if (formData.reason) data.append('reason', formData.reason);
      if (formData.attachment) data.append('attachment', formData.attachment);



      const result = await updateLeave(id, data);


      setSuccess(`Leave updated successfully! Total days: ${result.total_days || formData.total_days}`);
      setTimeout(() => navigate('/leave/history'), 2000);
    } catch (err) {
      setError(err.message || 'Failed to update leave');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading && !formData.leave_type) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/leave/history')}
          className="p-2 hover:bg-gray-100 rounded-lg transition"
        >
          <FiArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Edit Leave Request</h1>
          <p className="text-gray-500">Update your leave request details</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
        {/* Leave Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Leave Type
          </label>
          <select
            name="leave_type"
            value={formData.leave_type}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
          >
            <option value="casual">Casual Leave</option>
            <option value="sick">Sick Leave</option>
            <option value="unpaid">Unpaid Leave</option>
          </select>
        </div>

        {/* ✅ Dates with live calculation */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              name="start_date"
              value={formData.start_date}
              onChange={handleDateChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              name="end_date"
              value={formData.end_date}
              onChange={handleDateChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        {/* ✅ Total Days Display - Live Update */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FiCalendar className="w-5 h-5 text-indigo-500" />
              <div>
                <p className="text-sm text-gray-500">Total Working Days</p>
                <p className="text-2xl font-bold text-indigo-600">
                  {formData.total_days || 0}
                </p>
              </div>
            </div>
            {originalData && originalData.total_days !== formData.total_days && formData.total_days > 0 && (
              <div className="text-right">
                <p className="text-xs text-amber-600">
                  Was: {originalData.total_days} days
                </p>
                <p className="text-xs text-green-600">
                  Now: {formData.total_days} days
                </p>
              </div>
            )}
          </div>
          {formData.start_date && formData.end_date && formData.total_days === 0 && (
            <p className="text-xs text-red-500 mt-2">
              ⚠️ No working days selected. Please check your dates.
            </p>
          )}
        </div>

        {/* Reason */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Reason
          </label>
          <textarea
            name="reason"
            rows="4"
            value={formData.reason}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {/* Attachment */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Attachment (Optional)
          </label>
          <div className="flex items-center gap-4">
            <label className="flex-1 cursor-pointer">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-indigo-500 transition">
                <FiUpload className="w-6 h-6 text-gray-400 mx-auto mb-1" />
                <p className="text-sm text-gray-500">
                  {formData.attachment ? formData.attachment.name : 'Upload new file'}
                </p>
              </div>
              <input
                type="file"
                name="attachment"
                onChange={handleChange}
                className="hidden"
              />
            </label>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={() => navigate('/leave/history')}
            className="px-6 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || isLoading || formData.total_days === 0}
            className="flex-1 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LeaveEdit;